import styled, { Button } from "grabcad-ui-elements";
import { OrangeInfoBox } from "./OrangeInfoBox";
import { FormEvent, FunctionComponent } from "react";
import { TranslationFunction } from "../../../components/ApplicationProvider";
import { VersionManagementGroupsTable } from "./VersionManagementGroupsTable";
import { VersionManagementCompanyTable } from "./VersionManagementCompanyTable";
import { Company, Group, SoftwareTagWithUpdateStrategy } from "../types/version_management_types";
import { useVersionManagementState } from "../hooks/useVersionManagementState";
import { UpdateStrategy } from "@grabcad/company-server-shared/dist/types";

export interface VersionManagementFormProps {
    company: Company;
    groups?: Group[];
    softwareTags: SoftwareTagWithUpdateStrategy[];
    updateVersions: (data: UpdateVersionsData) => void;
    isUpdatingVersions: boolean;
    isReadOnly?: boolean;
    t: TranslationFunction;
}

export interface UpdateVersionsData {
    company: undefined | { updateStrategy?: UpdateStrategy; tagId?: number };
    groups: {
        groupId: number;
        updateStrategy: null | UpdateStrategy;
        tagId: null | number;
    }[];
}

/**
 * Form for selecting the GrabCAD Print version
 */
export const VersionManagementForm: FunctionComponent<VersionManagementFormProps> = ({
    softwareTags,
    t,
    updateVersions,
    isUpdatingVersions,
    isReadOnly,
    company,
    groups = [],
}) => {
    const { companyState, groupState, setCompanyVersion, setGroupVersion, hasChanges, reset } =
        useVersionManagementState(company, groups);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isReadOnly) {
            return;
        }

        const companyData =
            companyState.nextVersion?.updateStrategy || companyState.nextVersion?.tagId
                ? {
                      updateStrategy: companyState.nextVersion?.updateStrategy,
                      tagId: companyState.nextVersion?.tagId,
                  }
                : undefined;

        const groupsData: UpdateVersionsData["groups"] = groupState
            .filter((x) => x.nextVersion !== undefined)
            .map((x) => ({
                groupId: x.id,
                updateStrategy: x.nextVersion?.updateStrategy || null,
                tagId: x.nextVersion?.tagId || null,
            }));

        updateVersions({ company: companyData, groups: groupsData });
    };

    return (
        <Form style={{}} onSubmit={handleSubmit}>
            <H2>{t("version_management.select_grabcad_print_version")}</H2>

            <h4>{t("version_management.company_wide_heading")}</h4>

            <VersionManagementCompanyTable
                softwareTags={softwareTags}
                company={companyState}
                onChange={setCompanyVersion}
                isReadOnly={isReadOnly}
            />

            <h4>{t("version_management.per_group_heading")}</h4>

            <OrangeInfoBox
                initiallyCollapsed={true}
                heading={t("version_management.users_with_multiple_groups")}
            />

            <VersionManagementGroupsTable
                groups={groupState}
                onGroupVersionChange={setGroupVersion}
                softwareTags={softwareTags}
                isReadOnly={isReadOnly}
            />

            <Buttons>
                <Button
                    data-testid={`version-management-cancel${hasChanges ? "" : "-disabled"}`}
                    id="qa-company-cancel"
                    type="button"
                    secondary
                    disabled={!hasChanges || isReadOnly}
                    onClick={reset}
                >
                    {t("forms.cancel")}
                </Button>
                <Button
                    data-testid={`version-management-submit${hasChanges ? "" : "-disabled"}`}
                    id="qa-company-submit"
                    loading={isUpdatingVersions}
                    type="submit"
                    className="primary right floated"
                    disabled={!hasChanges || isUpdatingVersions || isReadOnly}
                >
                    {t("forms.submit")}
                </Button>
            </Buttons>
        </Form>
    );
};

const Form = styled.form`
    max-width: 550px;
    padding-bottom: 25px;
    margin-top: 32px;
`;

const Buttons = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
`;

const H2 = styled.h2`
    height: 22px;
    margin-bottom: 32px;
    font-size: 18px;
    font-weight: 700;
`;
